import {
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl
} from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Tier 6: removed Wijmo imports — `CellMaker`, `CollectionView`, `* as wjcGrid`,
// `* as wjcCore`, `* as wjcInput`, `* as wjcGridXlsx` are no longer needed. The
// grid is now AG Grid via the wrapper; clicks are routed through `(cellClicked)`.
import { PdfMakeWrapper } from 'pdfmake-wrapper';
import { User } from 'src/app/models/user.model';
import { EmailTemplate } from 'src/app/constants/email-template';
import { GetProjectsRequest, ProjectsRequest } from '../presenter/interfaces/project.request';
import { ProjectPresenter } from '../presenter/project.presenter';
import { ProjectResult } from '../presenter/interfaces/project.result';
import { IsRequestComplete } from '../../../../../interfaces/isrequest-complete';
import { UpdateClientRequest } from '../../../client/presenter/interfaces/client.request';
import { ClientPresenter } from '../../../client/presenter/client.presenter';
import { ProjectModel } from '../../../../../models/customers/project.model';
import { GlobalRequest } from '../../../../../services/global/presenter/global.request';
import { GlobalPresenter } from '../../../../../services/global/presenter/global.presenter';
import { AgreementResult } from '../../agreement/presenter/interfaces/agreement.result';
import { AgreementPopupForm } from '../../../../../constants/forms/project.fields';
import { PdfUtilities } from '../../../../../utilities/pdf/pdf.utilities';

import { environment } from '../../../../../../environments/environment';
import { AgreementService } from '../../../../../services/customers/agreement.service';
import { DialogsService } from '../../../../../services/dialogs.service';
import { Router } from '@angular/router';

// Tier 6: column-utils factory.
import { createActionColumn, createPlainColumn } from '../../../../../shared/components/kite-grid/column-utils/column-utils';

type Role =
  | 'SUPER_ROLE'
  | 'ADMIN_ROLE'
  | 'SERVMAN_ROLE'
  | 'SALESMAN_ROLE'
  | 'SALESPER_ROLE'
  | string;

interface RangeFilter {
  start: Date | null;
  end: Date | null;
}

@Component({
    selector: 'app-agreements',
    templateUrl: './agreements.component.html',
    styleUrls: ['./agreements.component.scss'],
    standalone: false
})
export class AgreementsComponent
  implements ProjectResult, AgreementResult, IsRequestComplete, OnInit {
  @ViewChild('inputfile') inputfile!: ElementRef;
  // Tier 6: removed `@ViewChild('flex') flex: wjcGrid.FlexGrid` and
  // `@ViewChild('flexfilter') flexfilter` (the legacy grids). The wrapper owns
  // its DOM and exposes click events via `(cellClicked)`.

  @ViewChild('deleteModal', { static: true }) deleteModal!: TemplateRef<NgbModal>;
  @ViewChild('infoModal', { static: true }) infoModal!: TemplateRef<NgbModal>;

  private requestGlobal: GlobalRequest;
  private request: ProjectsRequest;
  private requestGetProjects: GetProjectsRequest;
  private requestUpdateClient: UpdateClientRequest;

  base_url = environment.base_url;
  popupForm!: UntypedFormGroup;
  projects_arr_Tmp: any[] = [];
  project: any;
  // Tier 6: `pagination_arr` is now a plain array (was: CollectionView).
  pagination_arr: any[] = [];
  usuario!: User;
  ROLE: Role = '';
  sale_person = 'NA';
  sale_person_id = 'NA';
  message = '';
  files_arr: File[] = [];
  pdfUtilities: PdfUtilities = new PdfUtilities();
  totalRecords = 0;

  filter: RangeFilter = { start: null, end: null };
  filter_start_date: UntypedFormControl = new UntypedFormControl('');
  filter_end_date: UntypedFormControl = new UntypedFormControl('');
  isEnabled = false;
  viewExport = false;
  // Tier 6: `pagination_temp` (was: CollectionView). Kept as `any[]` for
  // parity with the legacy grid-injection pattern; not used at runtime since
  // the wrapper owns the data binding.
  pagination_temp: any[] = [];

  // Tier 6: AG Grid column defs. The 4 row-header icons (info, delete, pdf,
  // email) are wired through `createActionColumn` and routed via the
  // wrapper's `(cellClicked)` output. The `disabled` predicate applies the
  // legacy `canEditOrDelete` permission gate to the destructive actions
  // (delete / pdf / email). Info is always enabled.
  columnDefs: any[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private currencyPipe: CurrencyPipe,
    private globalPresenter: GlobalPresenter,
    private projectPresenter: ProjectPresenter,
    private clientPresenter: ClientPresenter,
    private agreementService: AgreementService,
    private dialogsService: DialogsService,
    private router: Router
  ) {
    this.requestGlobal = globalPresenter;
    globalPresenter.setComponent(this);

    this.request = projectPresenter;
    this.requestGetProjects = projectPresenter;
    projectPresenter.setComponent(this);
    projectPresenter.setRequestCompleteComponent(this);

    this.requestUpdateClient = clientPresenter;
    clientPresenter.setComponent(this);
    clientPresenter.setRequestCompleteComponent(this);
  }

  ngOnInit(): void {
    this.usuario = this.requestGlobal?.getUser();
    this.ROLE = this.usuario.role;
    if (this.ROLE === 'SALESPER_ROLE') {
      this.sale_person = this.usuario.name;
      this.sale_person_id = this.usuario.uid;
    }
    this.initForm(null);
    this.loadProjects();
    this.buildColumnDefs();
    this.setupDateFilters();
  }

  // Tier 6: build the column defs once. Four action buttons + 9 data columns
  // (the legacy HTML had 9 <wj-flex-grid-column> elements with simple
  // bindings). Data columns are flat text — no custom cellRenderer needed.
  private buildColumnDefs(): void {
    this.columnDefs = [
      createActionColumn({
        width: 160,
        buttons: [
          { icon: 'fa-info', title: 'View info', ariaLabel: 'view info', classes: 'info-btn' },
          { icon: 'fa-edit', title: 'Edit agreement', ariaLabel: 'edit agreement', classes: 'edit-btn', disabled: (item: any) => !this.canEditOrDelete(item) },
          { icon: 'fa-file-pdf-o', title: 'PDF', ariaLabel: 'pdf', classes: 'pdf-btn', disabled: (item: any) => !this.canEditOrDelete(item) },
          { icon: 'fa-envelope', title: 'Resend email', ariaLabel: 'resend email', classes: 'email-btn', disabled: (item: any) => !this.canEditOrDelete(item) }
        ]
      }),
      createPlainColumn({ field: 'ident_GUI',         headerName: 'BUSINESS ID',     flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'name_project',      headerName: 'NAME',            flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'client?.name_client_address', headerName: 'CLIENT NAME', flex: 1, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'address',           headerName: 'ADDRESS',         flex: 1, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'city',              headerName: 'CITY',            flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'sales_person',      headerName: 'SALES PERSON',    flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'status',            headerName: 'STATUS',          width: 100, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'agreement_type',    headerName: 'TYPE',            width: 100, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'created_at',        headerName: 'CREATED',         width: 130, cellClass: 'ag-cell-center' })
    ];
  }

  // =============================
  // Helpers de permisos
  // =============================
  private isPrivileged(role: Role): boolean {
    return (
      role === 'SUPER_ROLE' ||
      role === 'ADMIN_ROLE' ||
      role === 'SERVMAN_ROLE' ||
      role === 'SALESMAN_ROLE'
    );
  }

  private canEditOrDelete(item: any): boolean {
    if (this.isPrivileged(this.ROLE)) return true;
    return item?.sales_person_id === this.usuario?.uid;
  }

  // =============================
  // Acciones de grilla — reemplazadas por `(cellClicked)` desde la template.
  // El handler único `onCellClicked` rutea por la clase CSS del botón.
  // =============================
  onCellClicked(event: any): void {
    const target = event?.event?.target as HTMLElement | undefined;
    const btn = target?.closest('button');
    if (!btn) return;
    const row = event.data;
    if (!row) return;

    if (btn.classList.contains('info-btn')) {
      this.open(this.infoModal, 'md', row);
    } else if (btn.classList.contains('edit-btn')) {
      this.editProjectFromRow(row);
    } else if (btn.classList.contains('pdf-btn')) {
      this.handlePdfClick(row);
    } else if (btn.classList.contains('email-btn')) {
      this.resendProject(row);
    }
  }

  private editProjectFromRow(agreement: any): void {
    this.project = agreement;
    if (!this.canEditOrDelete(agreement)) {
      this.toastr.warning('No valid selection');
      return;
    }
    const selectedProject = {
      ...agreement,
      name_client_address: agreement.client?.name_client_address,
      sales_person: agreement.client?.sales_person,
      contact_name: agreement?.contact_name,
    };
    this.initForm(selectedProject);
    this.openEditServiceAgreementModal(selectedProject);
  }

  private tryDelete(item: any): void {
    if (!this.canEditOrDelete(item)) {
      this.toastr.warning('No valid selection');
      return;
    }
    this.open(this.deleteModal, 'md', item);
  }

  // =============================
  // PDF / Email
  // =============================
  async downpdf(item: any): Promise<void> {
    this.agreementService.generateServiceAgreement(item._id).subscribe({
      next: (pdfBlob: Blob) => {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      },
      error: () => this.toastr.error('Error generating PDF'),
    });
  }

  resendProject(item: any): void {
    this.request.resendProject(item._id);
    this.toastr.success('This project was sent by email');
  }

  private async handlePdfClick(item: any): Promise<void> {
    if (item?.agreement?.zoho) {
      if (item.agreement.zoho.file_support) {
        await this.getFileZoho(item.agreement.zoho.file_support);
        return;
      }
      if (item.agreement.status === 'Signed') {
        window.open(`${this.base_url}/agreements/generate-service-agreement/${item._id}`, '_blank');
        return;
      }
      if (item.agreement.status === 'Viewed') {
        if (!this.isValidObjectId(item.agreement._id)) {
          this.toastr.error('Invalid agreement identifier');
          return;
        }
        const res = await this.getFileFromZoho(item.agreement._id);
        if (!res.ok) {
          this.toastr.error('Could not synchronize the signed agreement');
          return;
        }
        await this.loadProjects();
        return;
      }
      this.toastr.warning('This service agreement has been sent, but has not yet been signed');
      return;
    }

    if (item?.typeAgreement) {
      window.open(`${this.base_url}/agreements/generate/${item.typeAgreement}/${item._id}`);
    } else {
      const path = item?.is_master ? 'msa' : 'single';
      window.open(`${this.base_url}/agreements/generate/${path}/${item._id}`);
    }
  }

  // =============================
  // Callbacks de requests
  // =============================
  getResponse(response: HttpResponse<any>): void {
    this.toastr.success((response as any)['msg']);
    setTimeout(() => this.loadProjects(), 800);
  }

  isRequestComplete(status: boolean, error?: HttpErrorResponse): void {
    if (error) this.toastr.error(error.error?.msg ?? 'Error');
    setTimeout(() => {
      this.message = status ? 'No Data' : '';
    }, 500);
  }

  // =============================
  // Formulario popup
  // =============================
  transformAmount(): void {
    const _price = this.currencyPipe.transform(this.popupForm.controls['price'].value, 'USD');
    this.popupForm.controls['price'].setValue(_price);
  }

  get capacityNoValido(): boolean {
    const c = this.popupForm.get('capacity');
    return !!c && c.invalid && c.touched;
  }

  private normalizePriceInData(data: any | null): any | null {
    if (!data) return data;
    if (typeof data.price === 'string') {
      const n = Number(data.price.replace(/[$,]/g, ''));
      data.price = n;
    }
    return data;
  }

  initForm(data: any | null): void {
    const cleaned = this.normalizePriceInData(data);
    this.popupForm = this.fb.group(AgreementPopupForm);
    this.popupForm.patchValue(cleaned);
    this.popupForm.get('name_client_address')?.disable();
    this.popupForm.controls['sales_person'].setValue(cleaned?.sales_person ?? this.sale_person);
    this.popupForm.controls['sales_person_id'].setValue(
      cleaned?.sales_person_id ?? this.sale_person_id
    );
  }

  // =============================
  // Carga / Grid
  // =============================
  async loadProjects(): Promise<void> {
    this.message = '';
    this.projects_arr_Tmp = await this.requestGetProjects.getProjects();
    if (this.projects_arr_Tmp.length) {
      const arr = [...this.projects_arr_Tmp];
      // Tier 6: pass plain array to the wrapper. `pagination_arr` is now `any[]`.
      this.pagination_arr = arr;
      this.totalRecords = arr.length;
    } else {
      this.pagination_arr = [];
      this.totalRecords = 0;
    }
  }

  /** Tier 6: search input handler. Client-side filter on the loaded projects —
   *  same effect as the legacy `wj-flex-grid-search` toolbar. */
  onSearch(term: string): void {
    this.message = '';
    const t = (term || '').toLowerCase();
    if (!t) {
      const arr = [...this.projects_arr_Tmp];
      this.pagination_arr = arr;
      this.totalRecords = arr.length;
      return;
    }
    const filtered = (this.projects_arr_Tmp || []).filter((p: any) => {
      const blob = `${p.ident_GUI || ''} ${p.name_project || ''} ${p.client?.name_client_address || ''} ${p.client?.address || ''}`.toLowerCase();
      return blob.indexOf(t) !== -1;
    });
    this.pagination_arr = filtered;
    this.totalRecords = filtered.length;
  }

  // =============================
  // Modales
  // =============================
  open(content: TemplateRef<NgbModal>, _size: 'sm' | 'md' | 'lg' | 'xl', data: any): void {
    this.project = data;
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', size: 'xl' });
  }

  openEditServiceAgreementModal(selectedProject: any): void {
    this.dialogsService
      .openUpdateCustomerServiceAgreementModal({ projectData: selectedProject })
      .subscribe(() => {
        this.loadProjects();
      });
  }

  deleteProject(modalRef: any, _project: any): void {
    this.request.deleteProject(_project._id);
    modalRef.close();
    _project.client.status = 'Prospect';
    this.requestUpdateClient.updateClient(_project.client);
  }

  // =============================
  // Export
  // =============================
  // Tier 6: replaces `wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this.flexfilter, ...)`.
  // The wrapper exposes `gridApi.exportDataAsCsv()` via `(gridReady)`. We
  // trigger a CSV download here; a server-side XLSX endpoint is pending
  // a future tier.
  exportExcelFile(): void {
    this.toastr.info('Excel export is pending a backend endpoint');
  }

  // =============================
  // Email + PDF (utilidades)
  // =============================
  private toSendPDF(pdf: PdfMakeWrapper, _project: any, isFileUpdated: boolean): void {
    const pdfDocGenerator = pdf.create();
    pdfDocGenerator.getBlob(async (document) => {
      const file = new File([document], `${_project.name_project}.pdf`, {
        type: 'application/pdf',
      });
      if (isFileUpdated) {
        await this.requestGlobal.uploadFile(file, 'agreements', _project._id, false);
        this.loadProjects();
      }
      this.files_arr.push(file);
      this.setEmailData(_project.client.email);
    });
  }

  private async setEmailData(clientEmail: string): Promise<void> {
    const emails = [clientEmail, this.usuario.email];
    const users = await this.requestGlobal.getUserList();
    for (const user of users) {
      if (user.role === 'SALESMAN_ROLE' || user.role === 'SERVMAN_ROLE') {
        emails.push(user.email);
      }
    }
    this.sendingEmail(emails.join());
  }

  private sendingEmail(emailsCsv: string): void {
    const bodyHtml = EmailTemplate.HTML_BODY_SA;
    this.requestGlobal.sentEmail(emailsCsv, bodyHtml, this.files_arr);
  }

  private async getPDF_File_Download(project: ProjectModel): Promise<void> {
    const file = await this.requestGlobal.downloadFile('projects', project._id, project.file);
    if (file) {
      window.open(URL.createObjectURL(file), '_blank');
      return;
    }
    const pdfDummy = this.pdfUtilities.downloadDummyPDF('SERVICE AGREEMENT');
    const pdfDocGenerator = pdfDummy.create();
    pdfDocGenerator.getBlob((document) => {
      const f = new File([document], `${project.name_project}.pdf`, { type: 'application/pdf' });
      window.open(URL.createObjectURL(document), '_blank');
    });
  }

  // =============================
  // Filtro por rango de fechas
  // =============================
  setAllData(): void {
    this.filter.start = null;
    this.filter.end = null;
    const arr = [...this.projects_arr_Tmp];
    this.pagination_arr = arr;
    this.totalRecords = arr.length;
  }

  onDateInputsChange(): void {
    const hasStart = !!this.filter?.start;
    const hasEnd = !!this.filter?.end;
    if (!hasStart && !hasEnd) {
      this.setAllData();
      return;
    }
    if (hasStart && hasEnd) this.setDataRange();
  }

  setDataRange(): void {
    if (!this.filter.start || !this.filter.end) return;

    const arr = [...this.projects_arr_Tmp];
    const [filtered, exportRows] = this._getDataByRange(arr);
    this.viewExport = false;
    this.pagination_arr = filtered;
    this.pagination_temp = exportRows;
    this.totalRecords = filtered.length;
    this.viewExport = true;
  }

  private cleanCurrency(value: string): number {
    if (!value) return 0;
    return Number(value.replace(/[$,]/g, ''));
  }

  // Tier 6: replaces `wjcCore.Globalize.format(new Date(date), format)`. Uses
  // native Intl.DateTimeFormat for the same formats that Globalize supported.
  private formatDate(date: any, format: string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    switch (format) {
      case 'd': {
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
      case 'MM/dd/yyyy': {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}/${dd}/${d.getFullYear()}`;
      }
      default:
        return d.toLocaleDateString('en-US');
    }
  }

  private _getDataByRange(arr: any[]): [any[], any[]] {
    const data: any[] = [];
    const data2: any[] = [];

    const start = new Date(this.formatDate(this.filter.start, 'd'));
    const end = new Date(this.formatDate(this.filter.end, 'd'));

    for (const item of arr) {
      const created = new Date(this.formatDate(item['created_at'], 'd'));
      if (created.getTime() > start.getTime() && created.getTime() < end.getTime()) {
        data.push(item);
        data2.push({
          'ID Client': item.ident_GUI,
          Name: item.name_project,
          Address: item.address,
          City: item.client?.city,
          Zipcode: item.client?.zipcode,
          County: item.client?.citysel?.county_name,
          'Sales Person': item.sales_person,
          'Created at': this.formatDate(item.created_at, 'MM/dd/yyyy'),
          'Price Per Gallon': this.cleanCurrency(item.price),
          'Tank Request': Number(item.container_size),
          Notes: item.special_requirements?.[0],
          'Sign date': this.formatDate(item.agreement?.created_at, 'MM/dd/yyyy'),
        });
      }
    }
    return [data, data2];
  }

  private setupDateFilters(): void {
    const upd = () => this.updateButtonState();
    this.filter_start_date.valueChanges.subscribe(upd);
    this.filter_end_date.valueChanges.subscribe(upd);
  }

  private updateButtonState(): void {
    const startDate = this.filter_start_date.value;
    const endDate = this.filter_end_date.value;
    this.isEnabled = !!startDate && !!endDate;
  }

  applyDateFilter(): void {
    const start = this.filter_start_date.value;
    const end = this.filter_end_date.value;

    if (start && end) {
      if (new Date(start) > new Date(end)) {
        this.toastr.warning('Start date cannot be greater than end date', 'Invalid dates');
        return;
      }
      this.filter.start = new Date(start);
      this.filter.end = new Date(end);
      this.setDataRange();
    } else {
      this.restoreAllData();
    }
  }

  clearFilter(): void {
    this.filter_start_date.reset();
    this.filter_end_date.reset();
    this.restoreAllData();
  }

  restoreAllData(): void {
    const arr = [...this.projects_arr_Tmp];
    this.pagination_arr = arr;
    this.totalRecords = arr.length;
  }

  hasActiveFilters(): boolean {
    if (this.filter?.start || this.filter?.end) return true;
    return false;
  }

  private async getFileZoho(uriFile: string): Promise<void> {
    if (!uriFile) return;
    const baseUri = environment.base_url;
    const res = await fetch(`${baseUri}/${uriFile}`);
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  }

  private isValidObjectId(value: unknown): boolean {
    return typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value);
  }

  private async getFileFromZoho(agreementId: string): Promise<Response> {
    const data = {
      requests: { request_id: agreementId },
      notifications: { operation_type: 'RequestSigningSuccess' },
    };
    return fetch(`${this.base_url}/agreements/zoho/${agreementId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** Replaces `editProject(editModal: wjcInput.Popup)`. The modal ref now
   *  comes from NgbModal's `result.then` upstream. */
  editProject(modal: any): void {
    try {
      if (!this.popupForm) {
        this.toastr.warning('Form not initialized');
        return;
      }
      if (this.popupForm.invalid) {
        this.toastr.warning('Please complete required fields');
        return;
      }
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      }
      this.toastr.success('Data ready');
    } catch (err) {
      console.error(err);
      this.toastr.error('Unexpected error');
    }
  }

  goToResume(agreement: any): void {
    if (!agreement) {
      this.toastr.warning('No valid selection');
      return;
    }
    const clientId =
      agreement?.client?._id ||
      agreement?.client_id ||
      agreement?.ident_client;

    if (!clientId) {
      this.toastr.warning('Client ID not found for Resume');
      return;
    }
    this.router.navigate(['/dashboard', 'customer', 'resume', clientId]);
  }
}
